<template>
  <!-- Backdrop: click outside the drawer to dismiss. Sits below the panel
       but above the chat so taps on chat are blocked while the drawer is open. -->
  <div
    v-if="modelValue"
    class="sessions-drawer-scrim"
    @click="emit('update:modelValue', false)"
  />

  <div class="sessions-drawer d-flex flex-column" :class="{ open: modelValue }">
    <div class="drawer-header d-flex align-center pa-1 ga-1 flex-shrink-0">
      <v-btn
        v-if="showSessions"
        size="small"
        :color="activeTab === 'sessions' ? 'primary' : undefined"
        :variant="activeTab === 'sessions' ? 'tonal' : 'text'"
        @click="setTab('sessions')"
      >
        <v-icon start>mdi-chat-outline</v-icon>
        {{ t('drawer.sessions') }}
      </v-btn>
      <v-btn
        v-if="showSkills"
        size="small"
        :color="activeTab === 'skills' ? 'primary' : undefined"
        :variant="activeTab === 'skills' ? 'tonal' : 'text'"
        @click="setTab('skills')"
      >
        <v-icon start>mdi-lightning-bolt-outline</v-icon>
        {{ t('drawer.skills') }}
      </v-btn>
      <v-btn
        size="small"
        :color="activeTab === 'settings' ? 'primary' : undefined"
        :variant="activeTab === 'settings' ? 'tonal' : 'text'"
        @click="setTab('settings')"
      >
        <v-icon start>mdi-cog-outline</v-icon>
        {{ t('drawer.settings') }}
      </v-btn>
      <v-spacer />
      <v-btn
        icon="mdi-close"
        size="small"
        variant="text"
        :aria-label="t('common.close')"
        @click="emit('update:modelValue', false)"
      />
    </div>

    <v-divider />

    <!-- Tab panes use v-show rather than v-window: v-window's internal
         `.v-window__container { height: inherit }` resolves to `auto` when
         the parent (this flex column) has no explicit CSS height, which
         collapses the panes and hides the Skills list. The pane class
         applies `display: flex` directly (not via Vuetify's d-flex utility)
         because `d-flex` is `!important` and would beat v-show's inline
         `display: none`, leaving every pane visible at once. -->
    <div class="drawer-tabs flex-grow-1 d-flex flex-column overflow-hidden">
      <div
        v-if="showSessions"
        v-show="activeTab === 'sessions'"
        class="drawer-pane"
      >
        <div class="flex-grow-1 overflow-y-auto">
          <v-progress-linear v-if="sessionsStore.loading" indeterminate />

          <v-alert
            v-else-if="sessionsStore.error"
            type="error"
            variant="tonal"
            density="compact"
            class="ma-3"
          >
            {{ sessionsStore.error }}
          </v-alert>

          <v-list v-else density="compact" nav>
            <v-list-item
              v-for="session in sessionsStore.sortedSessions"
              :key="session.id"
              :active="session.id === sessionsStore.activeSessionId"
              rounded="lg"
              @click="onSwitch(session.id)"
            >
              <v-list-item-title class="text-body-2 session-title">
                {{ formatTitle(session) }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption mt-1 d-flex align-center">
                <span>{{ formatDate(session.updatedAt) }}</span>
                <v-spacer />
                <span v-if="showCredits && session.creditsUsed > 0" class="session-credits d-inline-flex align-center">
                  <v-icon size="12" class="mr-1">mdi-credit-card-check-outline</v-icon>
                  {{ formatCredits(session.creditsUsed) }}
                </span>
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item v-if="sessionsStore.sortedSessions.length === 0">
              <v-list-item-title class="text-body-2 text-medium-emphasis">
                {{ t('drawer.noSessions') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <div
        v-if="showSkills"
        v-show="activeTab === 'skills'"
        class="drawer-pane"
      >
        <div class="pa-3 flex-shrink-0">
          <v-btn
            block
            color="primary"
            prepend-icon="mdi-plus"
            class="mb-2"
            :disabled="skillsLoading"
            @click="createSkill"
          >
            {{ t('drawer.newSkill') }}
          </v-btn>

          <v-text-field
            v-model="skillSearch"
            :placeholder="t('drawer.searchSkills')"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            density="compact"
            hide-details
            clearable
            @update:model-value="debouncedSkillSearch"
          />
        </div>

        <v-divider />

        <div class="flex-grow-1 overflow-y-auto">
          <v-progress-linear v-if="skillsLoading" indeterminate />

          <v-alert
            v-else-if="skillsError"
            type="error"
            variant="tonal"
            density="compact"
            class="ma-3"
            closable
          >
            {{ skillsError }}
          </v-alert>

          <div
            v-else-if="skillUserList.length === 0 && skillSharedList.length === 0 && skillBuiltInList.length === 0"
            class="pa-4 text-body-2 text-medium-emphasis"
          >
            {{ skillSearch ? t('drawer.noSkillsMatch') : t('drawer.noSkillsYet') }}
          </div>

          <v-list v-else density="compact" nav>
            <v-list-subheader v-if="skillUserList.length">{{ t('drawer.mySkills') }}</v-list-subheader>
            <v-list-item
              v-for="skill in skillUserList"
              :key="skill.name"
              rounded="lg"
              @click="editSkill(skill.name, false)"
            >
              <v-list-item-title class="text-body-2">{{ skill.name }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ skill.description }}</v-list-item-subtitle>
            </v-list-item>

            <v-list-subheader v-if="skillSharedList.length">{{ t('drawer.shared') }}</v-list-subheader>
            <v-list-item
              v-for="skill in skillSharedList"
              :key="'shared-' + skill.name"
              rounded="lg"
              @click="editSkill(skill.name, true)"
            >
              <v-list-item-title class="text-body-2">
                {{ skill.name }}
                <v-icon size="x-small" color="primary" class="ml-1">mdi-account-group</v-icon>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ skill.description }}</v-list-item-subtitle>
            </v-list-item>

            <!-- Built-ins open in the same dialog in read-only mode (the API
                 flags them readOnly); the shared=true lookup covers them. -->
            <v-list-subheader v-if="skillBuiltInList.length">{{ t('drawer.builtIn') }}</v-list-subheader>
            <v-list-item
              v-for="skill in skillBuiltInList"
              :key="'global-' + skill.name"
              rounded="lg"
              @click="editSkill(skill.name, true)"
            >
              <v-list-item-title class="text-body-2">
                {{ skill.name }}
                <v-icon size="x-small" class="ml-1 text-medium-emphasis">mdi-lock-outline</v-icon>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ skill.description }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </div>

        <SkillEditorDialog ref="skillEditorDialog" />
      </div>

      <div
        v-show="activeTab === 'settings'"
        class="drawer-pane"
      >
        <div class="flex-grow-1 overflow-y-auto pa-1">
          <v-list density="compact" nav>
            <v-list-item @click="chatStore.toggleToolCalls()">
              <template #prepend>
                <v-icon>mdi-wrench-outline</v-icon>
              </template>
              <v-list-item-title class="text-body-2">{{ t('drawer.showToolCalls') }}</v-list-item-title>
              <template #append>
                <v-switch
                  :model-value="chatStore.showToolCalls"
                  hide-details
                  density="compact"
                  @update:model-value="chatStore.toggleToolCalls()"
                />
              </template>
            </v-list-item>
            <v-list-item @click="chatStore.toggleAutoAccept()">
              <template #prepend>
                <v-icon>mdi-shield-check-outline</v-icon>
              </template>
              <v-list-item-title class="text-body-2">{{ t('drawer.autoAcceptEdits') }}</v-list-item-title>
              <template #append>
                <v-switch
                  :model-value="chatStore.autoAcceptMutations"
                  hide-details
                  density="compact"
                  @update:model-value="chatStore.toggleAutoAccept()"
                />
              </template>
            </v-list-item>
            <v-list-item @click="themeToggle">
              <template #prepend>
                <v-icon>{{ isDark() ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
              </template>
              <v-list-item-title class="text-body-2">{{ t('drawer.darkMode') }}</v-list-item-title>
              <template #append>
                <v-switch
                  :model-value="isDark()"
                  hide-details
                  density="compact"
                  @update:model-value="themeToggle"
                />
              </template>
            </v-list-item>
          </v-list>

          <v-divider class="my-1" />

          <!-- Build stamp (date · commit), set by vite.config.js. Click copies
               it so a user can paste the exact build into a support ticket. -->
          <v-list density="compact" nav>
            <v-list-item :title="versionCopied ? t('drawer.versionCopied') : t('drawer.version')" @click="copyVersion">
              <template #prepend>
                <v-icon>{{ versionCopied ? 'mdi-check' : 'mdi-information-outline' }}</v-icon>
              </template>
              <v-list-item-subtitle class="text-caption version-stamp">{{ buildInfo.version }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <CreditUsagePanel
            v-if="showCredits && creditsStore.configured !== false"
            :usage="creditsStore.usage"
            :loading="creditsStore.loading"
            :error="creditsStore.error"
            @retry="creditsStore.load()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { nextTick, ref, computed, watch } from 'vue'
import { useSessionsStore } from '../../stores/sessions.js'
import { useChatStore } from '../../stores/chat.js'
import { useCreditsStore } from '../../stores/credits.js'
import { useSkills } from '../../composables/useSkills.js'
import { useTheme } from '../../composables/useTheme.js'
import SkillEditorDialog from '../common/SkillEditorDialog.vue'
import CreditUsagePanel from '../settings/CreditUsagePanel.vue'
import { buildInfo } from '../../version.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  tab: { type: String, default: 'sessions' },
  showSessions: { type: Boolean, default: true },
  showSkills: { type: Boolean, default: false },
  showCredits: { type: Boolean, default: false },
  // Whether the parent widget is in expanded layout. When false, clicking a
  // session emits `request-expand` first so the host can resize before the
  // conversation appears — the sidebar alone is too narrow for chat content.
  expanded: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'update:tab', 'switch', 'request-expand'])

const sessionsStore = useSessionsStore()
const chatStore = useChatStore()
const creditsStore = useCreditsStore()
const { toggle: themeToggle, isDark } = useTheme()
const skillEditorDialog = ref(null)

const {
  userSkills: skillUserList,
  sharedSkills: skillSharedList,
  builtInSkills: skillBuiltInList,
  loading: skillsLoading,
  error: skillsError,
  refresh: refreshSkills,
  load: loadSkill,
  save: saveSkill,
  remove: removeSkill,
} = useSkills()

const activeTab = computed({
  get: () => {
    // Settings is always available; fall back to it (or the other enabled tab)
    // when the requested tab's feature is disabled.
    if (props.tab === 'skills' && !props.showSkills) {
      return props.showSessions ? 'sessions' : 'settings'
    }
    if (props.tab === 'sessions' && !props.showSessions) {
      return props.showSkills ? 'skills' : 'settings'
    }
    return props.tab
  },
  set: (v) => emit('update:tab', v),
})

function setTab(t) { emit('update:tab', t) }

const skillSearch = ref('')
let searchTimeout = null
function debouncedSkillSearch(val) {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => refreshSkills(val || undefined), 300)
}

// Lazy-load each tab's data when the drawer opens or the tab changes.
watch(
  () => [props.modelValue, activeTab.value],
  ([open, tab]) => {
    if (!open) return
    if (tab === 'sessions' && props.showSessions) sessionsStore.load()
    if (tab === 'skills' && props.showSkills) refreshSkills()
    if (tab === 'settings' && props.showCredits) creditsStore.load()
  },
  { immediate: true },
)

// Best-effort after-turn refresh: spend changes every turn, so when a turn
// finishes (isStreaming true→false) while the drawer is open on the Settings
// tab, re-pull usage. Background polling is deliberately avoided.
watch(
  () => chatStore.isStreaming,
  (streaming, wasStreaming) => {
    if (wasStreaming && !streaming && props.modelValue && props.showCredits && activeTab.value === 'settings') {
      creditsStore.load()
    }
  },
)

async function onSwitch(id) {
  if (id === sessionsStore.activeSessionId) {
    emit('update:modelValue', false)
    return
  }
  try {
    await chatStore.loadSession(id)
    sessionsStore.setActive(id, sessionsStore.sessions.find(s => s.id === id) || null)
    emit('switch', id)
  } finally {
    emit('update:modelValue', false)
  }
}

// The skill editor is a wide v-dialog (max-width 900) and renders inside the
// widget's iframe, so in sidebar mode it would be clipped. Expand first so
// the dialog has room. nextTick lets the resize commit before we open.
async function ensureExpandedForSkill() {
  if (!props.expanded) {
    emit('request-expand')
    await nextTick()
  }
}

async function createSkill() {
  await ensureExpandedForSkill()
  const result = await skillEditorDialog.value.open()
  if (result?.action === 'save') {
    await saveSkill(result.form)
    skillEditorDialog.value.close()
  }
}

async function editSkill(name, shared) {
  await ensureExpandedForSkill()
  const skill = await loadSkill(name, shared)
  if (!skill) return
  const result = await skillEditorDialog.value.open(skill)
  if (!result) return
  if (result.action === 'save') {
    await saveSkill(result.form)
    skillEditorDialog.value.close()
  } else if (result.action === 'delete') {
    await removeSkill(result.name, result.shared)
    skillEditorDialog.value.close()
  }
}

const versionCopied = ref(false)
let versionCopiedTimer = null
async function copyVersion() {
  try {
    await navigator.clipboard.writeText(buildInfo.version)
    versionCopied.value = true
    clearTimeout(versionCopiedTimer)
    versionCopiedTimer = setTimeout(() => { versionCopied.value = false }, 1500)
  } catch { /* clipboard unavailable (insecure context / denied) — label just stays */ }
}

function formatTitle(session) {
  if (session.title) return session.title
  return t('drawer.chatFallbackTitle', { id: session.id.slice(0, 8) })
}

function formatCredits(n) {
  const v = Number(n) || 0
  if (v >= 10) return Math.round(v).toLocaleString()
  return String(Math.round(v * 10) / 10)
}

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
</script>

<style scoped>
/* Drawer overlays the chat rather than pushing it: the parent .rpilot-body
   is position: relative, so this absolute panel slides over the conversation
   from the right edge. Width-based show/hide was the previous approach but it
   shrank the chat column in standalone (full-width) deployments. */
.sessions-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(320px, 100%);
  background: rgb(var(--v-theme-surface));
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.08);
  transform: translateX(100%);
  transition: transform 200ms ease;
  z-index: 2;
  overflow: hidden;
}
.sessions-drawer.open {
  transform: translateX(0);
}
.sessions-drawer-scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.32);
  z-index: 1;
}
.session-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.session-credits {
  color: rgb(var(--v-theme-primary));
}
.version-stamp {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.drawer-pane {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
}
</style>
